/**
 * v3.21-1707 — New Application Text Notifications admin section.
 * Rendered inside TelephonyPage.  Backend endpoints enforce
 * phone_system_admin.
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Trash2, Plus, Send, RefreshCw } from 'lucide-react';

export default function NewApplicationNotificationSettings() {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [history, setHistory] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addLabel, setAddLabel] = useState('');
  const [addPhone, setAddPhone] = useState('');
  const [savingAdd, setSavingAdd] = useState(false);
  const [testing, setTesting] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        api.get('/phone-system/application-notifications/settings'),
        api.get('/phone-system/application-notifications/history?limit=25'),
      ]);
      setEnabled(!!s.data?.enabled);
      setRecipients(s.data?.recipients || []);
      setHistory(h.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load notification settings');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const toggleEnabled = async (v) => {
    setEnabled(v);
    try {
      await api.put('/phone-system/application-notifications/settings/enabled', { enabled: v });
    } catch (e) {
      toast.error('Failed to update'); setEnabled(!v);
    }
  };
  const addRecipient = async () => {
    setSavingAdd(true);
    try {
      await api.post('/phone-system/application-notifications/recipients',
        { label: addLabel.trim(), phone: addPhone.trim(), enabled: true });
      setAddOpen(false); setAddLabel(''); setAddPhone('');
      toast.success('Recipient added');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add recipient');
    } finally { setSavingAdd(false); }
  };
  const toggleRecipient = async (id, v) => {
    try {
      await api.put(`/phone-system/application-notifications/recipients/${id}/enabled`, { enabled: v });
      await refresh();
    } catch (e) { toast.error('Failed to update recipient'); }
  };
  const removeRecipient = async (id) => {
    if (!window.confirm('Remove this recipient?')) return;
    try {
      await api.delete(`/phone-system/application-notifications/recipients/${id}`);
      toast.success('Recipient removed');
      await refresh();
    } catch (e) { toast.error('Failed to remove recipient'); }
  };
  const sendTest = async (id) => {
    setTesting(id);
    try {
      const { data } = await api.post('/phone-system/application-notifications/test', { recipient_id: id });
      toast.success(`Test sent (${data.state})`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed');
    } finally { setTesting(''); }
  };

  return (
    <Card className="border-slate-200" data-testid="new-app-notification-settings">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">New Application Text Notifications</h3>
            <p className="text-xs text-slate-500 mt-0.5">Internal SMS alerts fired when a new application enters the Hub.</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-600">Automatic Alerts</span>
            <Switch checked={enabled} onCheckedChange={toggleEnabled} data-testid="alerts-enabled-toggle" />
            <Button size="sm" variant="ghost" className="h-7" onClick={refresh} data-testid="refresh-alerts-btn">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        <div className="border rounded">
          <div className="flex items-center justify-between px-3 py-2 border-b bg-slate-50">
            <span className="text-xs font-medium text-slate-700">Recipients</span>
            <Button size="sm" className="h-7 text-xs" onClick={() => setAddOpen(true)} data-testid="add-recipient-btn">
              <Plus className="h-3 w-3 mr-1" /> Add Recipient
            </Button>
          </div>
          {loading ? (
            <div className="p-3 text-xs text-slate-500">Loading…</div>
          ) : recipients.length === 0 ? (
            <div className="p-3 text-xs text-slate-500">No recipients configured.</div>
          ) : (
            <ul className="divide-y">
              {recipients.map(r => (
                <li key={r.id} className="flex items-center gap-2 px-3 py-2 flex-wrap" data-testid={`recipient-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-800 truncate">{r.label}</div>
                    <div className="text-xs text-slate-500">{r.phone_masked}</div>
                  </div>
                  <Switch checked={!!r.enabled} onCheckedChange={(v) => toggleRecipient(r.id, v)}
                          data-testid={`recipient-toggle-${r.id}`} />
                  <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!r.enabled || testing === r.id}
                          onClick={() => sendTest(r.id)} data-testid={`test-send-${r.id}`}>
                    <Send className="h-3 w-3 mr-1" />{testing === r.id ? 'Sending…' : 'Test'}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => removeRecipient(r.id)}
                          data-testid={`remove-recipient-${r.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border rounded">
          <div className="px-3 py-2 border-b bg-slate-50 text-xs font-medium text-slate-700">
            Recent Notifications
          </div>
          {history.length === 0 ? (
            <div className="p-3 text-xs text-slate-500">No notification history yet.</div>
          ) : (
            <table className="w-full text-xs" data-testid="notification-history-table">
              <thead className="bg-slate-50 text-slate-600">
                <tr><th className="text-left px-3 py-1">Time</th><th className="text-left px-3 py-1">Recipient</th><th className="text-left px-3 py-1">Application</th><th className="text-left px-3 py-1">Status</th></tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id} className="border-t">
                    <td className="px-3 py-1 text-slate-600">{(h.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                    <td className="px-3 py-1">{h.recipient_label} <span className="text-slate-400">{h.recipient_masked}</span></td>
                    <td className="px-3 py-1 text-slate-600">{h.application_id?.slice(0, 12)}…</td>
                    <td className="px-3 py-1 font-medium">{h.state}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CardContent>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-recipient-dialog">
          <DialogHeader><DialogTitle>Add Recipient</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Input placeholder="Name / Label" value={addLabel} onChange={e => setAddLabel(e.target.value)}
                    data-testid="add-recipient-label" />
            <Input placeholder="Mobile Number (US)" value={addPhone} onChange={e => setAddPhone(e.target.value)}
                    data-testid="add-recipient-phone" />
            <p className="text-[11px] text-slate-500">Preview enforces the single-approved-destination allowlist.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={addRecipient} disabled={!addLabel.trim() || !addPhone.trim() || savingAdd}
                    data-testid="submit-add-recipient">
              {savingAdd ? 'Adding…' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
